// src/pages/TripCostCalculatorPage.js
import React, { useState } from "react";
import { api } from "../scripts/api";

const DEFAULTS = {
  origin: "7530 114 Ave SE, Calgary, Alberta, Canada",
  fuelCostPerLitre: 1.65,
  hotelRate: 120,
  flightCost: 0,
  driverRate: 0.50,
  maxHoursPerDay: 12,
  avgSpeedKmh: 80,
};

// mpg → litres per km:  (1 / 1.60934) / mpg * 3.78541
const litresPerKm = (mpg) => (1 / 1.60934) / mpg * 3.78541;

export default function TripCostCalculatorPage() {
  const [origin, setOrigin] = useState(DEFAULTS.origin);
  const [destination, setDestination] = useState("");
  const [fuelCostPerLitre, setFuelCostPerLitre] = useState(DEFAULTS.fuelCostPerLitre);
  const [hotelRate, setHotelRate] = useState(DEFAULTS.hotelRate);
  const [flightCost, setFlightCost] = useState(DEFAULTS.flightCost);
  const [driverRate] = useState(DEFAULTS.driverRate);
  const [mpg, setMpg] = useState(6);
  const [avgSpeed, setAvgSpeed] = useState(DEFAULTS.avgSpeedKmh);
  const [maxHours, setMaxHours] = useState(DEFAULTS.maxHoursPerDay);

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    setError("");
    setResult(null);

    if (!origin.trim() || !destination.trim()) {
      setError("Origin and destination are required");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get("/freight/distance", {
        params: { origin: origin.trim(), destination: destination.trim() },
      });

      const distanceKm = Number(data.distanceKm);
      if (!Number.isFinite(distanceKm)) throw new Error("Invalid distance returned");

      const maxKmPerDay = avgSpeed * maxHours;
      const days = Math.ceil(distanceKm / maxKmPerDay);
      const hotelNights = Math.max(0, days - 1);

      const litresNeeded = distanceKm * litresPerKm(mpg);
      const fuelTotal = litresNeeded * fuelCostPerLitre;
      const hotelTotal = hotelNights * hotelRate;
      const driverTotal = distanceKm * driverRate;
      const flight = Number(flightCost) || 0;
      const grandTotal = fuelTotal + hotelTotal + driverTotal + flight;

      setResult({
        distanceKm,
        duration: data?.details?.duration?.text || "",
        days,
        hotelNights,
        litresNeeded,
        fuelTotal,
        hotelTotal,
        driverTotal,
        flightCost: flight,
        grandTotal,
      });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `$${n.toFixed(2)}`;

  return (
    <div style={{ padding: "24px", maxWidth: "700px", margin: "0 auto" }}>
      <h1 style={{ marginBottom: "24px" }}>Trip Cost Calculator</h1>

      {/* Locations */}
      <div className="card" style={{ marginBottom: "20px", padding: "20px" }}>
        <h3 style={{ marginBottom: "16px" }}>Route</h3>
        <div style={{ display: "grid", gap: "12px" }}>
          <div>
            <label className="muted" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
              Starting Location
            </label>
            <input
              className="input"
              type="text"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              placeholder="Starting address"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
              Ending Location
            </label>
            <input
              className="input"
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="Destination address"
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>

      {/* Costs */}
      <div className="card" style={{ marginBottom: "20px", padding: "20px" }}>
        <h3 style={{ marginBottom: "16px" }}>Trip Costs</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px" }}>
          <div>
            <label className="muted" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
              Fuel Cost ($/litre)
            </label>
            <input
              className="input"
              type="number"
              value={fuelCostPerLitre}
              onChange={(e) => setFuelCostPerLitre(parseFloat(e.target.value) || 0)}
              step="0.01"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
              Fuel Economy (mpg)
            </label>
            <input
              className="input"
              type="number"
              value={mpg}
              onChange={(e) => setMpg(parseFloat(e.target.value) || 6)}
              step="0.5"
              min="1"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
              Hotel Rate ($/night)
            </label>
            <input
              className="input"
              type="number"
              value={hotelRate}
              onChange={(e) => setHotelRate(parseFloat(e.target.value) || 0)}
              step="1"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
              Flight Home ($)
            </label>
            <input
              className="input"
              type="number"
              value={flightCost}
              onChange={(e) => setFlightCost(e.target.value)}
              step="1"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "12px" }}>
          <div>
            <label className="muted" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
              Avg Speed (km/h)
            </label>
            <input
              className="input"
              type="number"
              value={avgSpeed}
              onChange={(e) => setAvgSpeed(parseFloat(e.target.value) || 80)}
              step="5"
              min="10"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label className="muted" style={{ fontSize: 12, marginBottom: 4, display: "block" }}>
              Max Driving Hours/Day
            </label>
            <input
              className="input"
              type="number"
              value={maxHours}
              onChange={(e) => setMaxHours(parseFloat(e.target.value) || 12)}
              step="0.5"
              min="1"
              max="24"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <p className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          Driver mileage: $0.50/km &nbsp;|&nbsp; Max km/day: {(avgSpeed * maxHours).toFixed(0)}
        </p>
      </div>

      <button
        className="btn"
        onClick={calculate}
        disabled={loading}
        style={{ width: "100%", padding: "14px", fontSize: 15 }}
      >
        {loading ? "Calculating..." : "Calculate Trip Cost"}
      </button>

      {/* Results */}
      {result && (
        <div className="card" style={{ marginTop: "24px", padding: "20px" }}>
          <h3 style={{ marginBottom: "16px" }}>Trip Breakdown</h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", fontSize: 14 }}>
            <span className="muted">Distance</span>
            <span style={{ textAlign: "right" }}>{result.distanceKm.toFixed(1)} km</span>

            {result.duration && (
              <>
                <span className="muted">Google Est. Drive Time</span>
                <span style={{ textAlign: "right" }}>{result.duration}</span>
              </>
            )}

            <span className="muted">Travel Days ({avgSpeed} km/h, {maxHours} hr max)</span>
            <span style={{ textAlign: "right" }}>{result.days} day{result.days !== 1 ? "s" : ""}</span>

            <span className="muted">Hotel Nights</span>
            <span style={{ textAlign: "right" }}>{result.hotelNights}</span>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid var(--card-border)", margin: "16px 0" }} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", fontSize: 14 }}>
            <span className="muted">Fuel ({result.litresNeeded.toFixed(1)} L @ ${fuelCostPerLitre}/L)</span>
            <span style={{ textAlign: "right" }}>{fmt(result.fuelTotal)}</span>

            <span className="muted">Hotel ({result.hotelNights} night{result.hotelNights !== 1 ? "s" : ""} @ ${hotelRate}/night)</span>
            <span style={{ textAlign: "right" }}>{fmt(result.hotelTotal)}</span>

            <span className="muted">Driver Mileage ({result.distanceKm.toFixed(1)} km @ $0.50/km)</span>
            <span style={{ textAlign: "right" }}>{fmt(result.driverTotal)}</span>

            <span className="muted">Flight Home</span>
            <span style={{ textAlign: "right" }}>{fmt(result.flightCost)}</span>
          </div>

          <hr style={{ border: "none", borderTop: "2px solid var(--accent, #f97316)", margin: "16px 0" }} />

          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 700 }}>
            <span>Total Trip Cost</span>
            <span style={{ color: "var(--accent, #f97316)" }}>{fmt(result.grandTotal)}</span>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          color: "#f87171", marginTop: "16px", padding: "12px",
          background: "rgba(248,113,113,0.15)", borderRadius: "6px",
        }}>
          {error}
        </div>
      )}
    </div>
  );
}
